import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import {
	useSalesLoginMutation,
	useSalespersonChangePasswordMutation,
	useCoordinatorChangePasswordMutation,
	useSalesManagerChangePasswordMutation,
} from '../../store/api/authApi';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { setCredentials, completedSetup } from '../../store/authSlice';
import { Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import alforaaFullLogoDark from '../../assets/svg/logo/alforaa_full_logo_dark.svg';
import { ButtonSpinner } from '../../components/shared/SharedComponents';
import { identifyUser } from '../../services/analytics';

/** Where each sales role lands after signing in. */
export function salesHomeForRole(role: string): string {
	return role === 'appointment_coordinator'
		? '/sales/contact-list'
		: '/sales/dashboard';
}

export default function SalesLoginPage() {
	const navigate = useNavigate();
	useAuth();
	const dispatch = useAppDispatch();
	const [salesLogin, { isLoading: loading }] = useSalesLoginMutation();

	const [email, setEmail] = useState('');
	const [password, setPassword] = useState('');
	const [showPassword, setShowPassword] = useState(false);
	const [error, setError] = useState('');

	const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	const canSubmit = emailValid && password.length > 0 && !loading;

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		if (!canSubmit) return;
		try {
			const result = await salesLogin({
				email: email.trim(),
				password,
			}).unwrap();
			identifyUser(result.user, result.role);
			dispatch(
				setCredentials({
					user: result.user,
					role: result.role,
					accessToken: result.accessToken,
					refreshToken: result.refreshToken,
					mustChangePassword: result.mustChangePassword || false,
				}),
			);
			navigate(
				result.mustChangePassword
					? '/sales/change-password'
					: salesHomeForRole(result.role),
			);
		} catch {
			setError('Email or password is incorrect');
		}
	};

	return (
		<div
			className='min-h-screen aurora-bg flex flex-col lg:flex-row relative overflow-hidden'
			data-testid='sales-login-page'>
			<div
				aria-hidden
				className='pointer-events-none absolute top-[-100px] left-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(255,180,140,0.55), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>
			<div
				aria-hidden
				className='pointer-events-none absolute bottom-[-100px] right-[100px] w-[500px] h-[500px] rounded-full'
				style={{
					background:
						'radial-gradient(circle, rgba(220,200,255,0.6), transparent 70%)',
					filter: 'blur(20px)',
				}}
			/>

			<div className='lg:hidden flex items-center justify-center pt-8 pb-2 px-6 relative z-10'>
				<img
					src={alforaaFullLogoDark}
					alt='Alforaa'
					className='h-9 w-auto block'
				/>
			</div>

			<div className='hidden lg:flex flex-1 flex-col justify-between p-14 relative z-10'>
				<div className='flex items-center'>
					<img
						src={alforaaFullLogoDark}
						alt='Alforaa'
						className='h-10 w-auto block'
					/>
				</div>
				<div>
					<div className='text-[14px] text-aurora-coral font-semibold uppercase tracking-[0.04em]'>
						For the sales team
					</div>
					<div className='text-[56px] font-semibold tracking-[-0.04em] text-aurora-ink leading-[1.05] mt-3'>
						Every school,<br />
						every stage,<br />
						<span
							style={{
								background: 'linear-gradient(135deg, #FF7A45, #E8541F)',
								WebkitBackgroundClip: 'text',
								WebkitTextFillColor: 'transparent',
							}}>
							one pipeline.
						</span>
					</div>
					<div className='text-[16px] text-aurora-ink-soft leading-relaxed mt-4 max-w-[460px]'>
						Import leads, track visits, generate proposals, and get MOUs signed
						— without spreadsheets or WhatsApp decks.
					</div>
				</div>
				<div className='text-[12px] text-aurora-ink-soft'>
					<div className='font-semibold text-aurora-ink'>
						Alforaa Sales CRM
					</div>
					<div>From first visit to signed contract.</div>
				</div>
			</div>

			<div className='flex-1 flex items-center justify-center relative z-10 p-6 sm:p-10 lg:p-0'>
				<form
					onSubmit={handleSubmit}
					className='aurora-glass-strong rounded-[28px] p-7 sm:p-10 w-full max-w-[440px] animate-fade-in'>
					<div className='text-[26px] font-semibold text-aurora-ink tracking-[-0.025em]'>
						Welcome back
					</div>
					<div className='text-[13px] text-aurora-ink-mute mt-1'>
						Sign in to your sales workspace
					</div>

					<div className='mt-5'>
						<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
							Email
						</label>
						<div className='relative'>
							<Mail
								size={16}
								className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
							/>
							<input
								type='email'
								value={email}
								onChange={(e) => setEmail(e.target.value)}
								placeholder='you@alforaa.com'
								data-testid='sales-email-input'
								className='w-full pl-10 pr-4 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
							/>
						</div>
					</div>

					<div className='mt-4'>
						<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
							Password
						</label>
						<div className='relative'>
							<Lock
								size={16}
								className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
							/>
							<input
								type={showPassword ? 'text' : 'password'}
								value={password}
								onChange={(e) => setPassword(e.target.value)}
								placeholder='Enter your password'
								data-testid='sales-password-input'
								className='w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all'
							/>
							<button
								type='button'
								onClick={() => setShowPassword((v) => !v)}
								className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
								{showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
							</button>
						</div>
					</div>

					{error && (
						<p
							className='text-status-error text-[12px] text-center mt-4'
							data-testid='sales-login-error'>
							{error}
						</p>
					)}

					<button
						type='submit'
						disabled={!canSubmit}
						data-testid='sales-login-submit'
						className='w-full py-3 mt-5 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2'>
						{loading && <ButtonSpinner />}
						{loading ? 'Signing in…' : 'Sign in'}
						{!loading && <ArrowRight size={16} />}
					</button>
				</form>
			</div>
		</div>
	);
}

const pwInputCls =
	'w-full pl-10 pr-10 py-3 rounded-[12px] bg-white border-2 border-aurora-ink/20 shadow-[0_1px_2px_rgba(15,18,53,0.05)] text-[13.5px] text-aurora-ink placeholder:text-aurora-ink-faint focus:outline-none focus:ring-2 focus:ring-aurora-coral/40 focus:border-aurora-coral transition-all';

/**
 * Shown once, right after a salesperson signs in with the temporary password
 * their manager set for them. Mirrors the parent first-login flow.
 */
export function SalesChangePasswordPage() {
	const navigate = useNavigate();
	const dispatch = useAppDispatch();
	const role = useAppSelector((s) => s.auth.role);
	const [salespersonChange, { isLoading: spLoading }] =
		useSalespersonChangePasswordMutation();
	const [coordinatorChange, { isLoading: coLoading }] =
		useCoordinatorChangePasswordMutation();
	const [managerChange, { isLoading: mgrLoading }] =
		useSalesManagerChangePasswordMutation();
	const isLoading = spLoading || coLoading || mgrLoading;
	const [oldPassword, setOldPassword] = useState('');
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showOld, setShowOld] = useState(false);
	const [showNew, setShowNew] = useState(false);
	const [error, setError] = useState('');

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError('');
		if (newPassword !== confirmPassword) {
			setError('New passwords do not match');
			return;
		}
		if (newPassword.length < 8) {
			setError('New password must be at least 8 characters');
			return;
		}
		try {
			const changePassword =
				role === 'appointment_coordinator'
					? coordinatorChange
					: role === 'sales_manager'
						? managerChange
						: salespersonChange;
			await changePassword({ oldPassword, newPassword }).unwrap();
			dispatch(completedSetup());
			navigate(salesHomeForRole(role || 'salesperson'));
		} catch (err: any) {
			setError(err?.data?.detail || 'Could not change your password');
		}
	};

	const fields = [
		{
			key: 'old',
			label: 'Current password',
			placeholder: 'Your temporary password',
			value: oldPassword,
			set: setOldPassword,
			show: showOld,
			toggle: () => setShowOld((v) => !v),
		},
		{
			key: 'new',
			label: 'New password',
			placeholder: 'At least 8 characters',
			value: newPassword,
			set: setNewPassword,
			show: showNew,
			toggle: () => setShowNew((v) => !v),
		},
		{
			key: 'confirm',
			label: 'Confirm new password',
			placeholder: 'Re-enter your new password',
			value: confirmPassword,
			set: setConfirmPassword,
			show: showNew,
			toggle: () => setShowNew((v) => !v),
		},
	];

	return (
		<div
			className='min-h-screen aurora-bg flex items-center justify-center p-6'
			data-testid='sales-change-password-page'>
			<form
				onSubmit={handleSubmit}
				className='aurora-glass-strong rounded-[28px] p-7 sm:p-10 w-full max-w-[440px] animate-fade-in'>
				<img
					src={alforaaFullLogoDark}
					alt='Alforaa'
					className='h-9 w-auto block mb-6'
				/>
				<div className='text-[26px] font-semibold text-aurora-ink tracking-[-0.025em]'>
					Set your password
				</div>
				<div className='text-[13px] text-aurora-ink-mute mt-1'>
					Replace the temporary password your manager gave you.
				</div>

				{fields.map((f) => (
					<div key={f.key} className='mt-5'>
						<label className='text-[11.5px] font-medium text-aurora-ink-soft block mb-1.5'>
							{f.label}
						</label>
						<div className='relative'>
							<Lock
								size={16}
								className='absolute left-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint'
							/>
							<input
								type={f.show ? 'text' : 'password'}
								value={f.value}
								onChange={(e) => f.set(e.target.value)}
								placeholder={f.placeholder}
								required
								className={pwInputCls}
							/>
							<button
								type='button'
								onClick={f.toggle}
								className='absolute right-3 top-1/2 -translate-y-1/2 text-aurora-ink-faint hover:text-aurora-ink'>
								{f.show ? <EyeOff size={16} /> : <Eye size={16} />}
							</button>
						</div>
					</div>
				))}

				{error && (
					<div className='mt-4 text-[12.5px] text-status-error'>{error}</div>
				)}

				<button
					type='submit'
					disabled={isLoading}
					className='mt-6 w-full py-3 rounded-[12px] bg-coral-gradient text-white text-[14px] font-semibold shadow-coral hover:opacity-95 disabled:opacity-50 transition-all flex items-center justify-center gap-2'>
					{isLoading && <ButtonSpinner />}
					{isLoading ? 'Saving…' : 'Save and continue'}
					{!isLoading && <ArrowRight size={16} />}
				</button>
			</form>
		</div>
	);
}
